import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';

interface Msg { id: string; text: string; kind: string; senderAlias: string | null; senderRole: string | null; isYou: boolean; createdAt: string }
interface RosterEntry { memberId: string; alias: string | null; role: string; name?: string; isYou?: boolean }
interface JoinReq { memberId: string; role: string; name: string; phone: string | null }
interface RevealEntry { memberId: string; alias: string; role: string; name: string; phone: string | null }

const roleColor = (r: string | null) => (r === 'seller' ? '#0369a1' : r === 'buyer' ? '#7c3aed' : 'var(--accent-color)');

const MarketGroup: React.FC = () => {
  const { groupId = '' } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<any>(null);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [isMediator, setIsMediator] = useState(false);
  const [canPost, setCanPost] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [view, setView] = useState<'chat' | 'requests' | 'members' | 'offers' | 'deals'>('chat');
  const [requests, setRequests] = useState<JoinReq[]>([]);
  const [reveal, setReveal] = useState<RevealEntry[]>([]);
  const [offers, setOffers] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [offerForm, setOfferForm] = useState({ commodity: '', quantity: '', unit: '', price: '', note: '' });
  const [dealForm, setDealForm] = useState({ sellerMemberId: '', buyerMemberId: '', commodity: '', quantity: '', price: '', commissionAmount: '', commissionPercent: '', note: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const loadGroup = useCallback(async () => {
    try {
      const g = await apiService.marketGetGroup(groupId);
      const d = g?.data;
      setGroup(d?.group || null); setRoster(d?.roster || []); setIsMediator(!!d?.isMediator);
      setCanPost(!!d?.canPost); setPendingCount(d?.pendingCount || 0);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load the group'); }
  }, [groupId]);

  const loadMessages = useCallback(async () => {
    try { const r = await apiService.marketGetMessages(groupId); setMessages(r?.data?.messages || []); } catch { /* keep */ }
  }, [groupId]);

  useEffect(() => { (async () => { setLoading(true); await loadGroup(); await loadMessages(); setLoading(false); })(); }, [loadGroup, loadMessages]);

  // Realtime: new messages (alias-only payloads).
  useEffect(() => {
    if (!groupId || !canPost) return;
    const off = subscribeToChannel(`market:${groupId}`, ['market:message:new'], (_ev, data) => {
      const m: Msg | undefined = data?.message;
      if (!m?.id) return;
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    });
    return () => { try { off(); } catch { /* noop */ } };
  }, [groupId, canPost]);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [messages]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try { const r = await apiService.marketSendMessage(groupId, body); const m = r?.data?.message; if (m?.id) setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m])); setText(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not send'); }
    finally { setSending(false); }
  };

  const requestJoin = async (role: 'seller' | 'buyer') => {
    try { await apiService.marketRequestJoin(groupId, role); await loadGroup(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not send request'); }
  };

  const openRequests = async () => { setView('requests'); try { const r = await apiService.marketListRequests(groupId); setRequests(r?.data?.requests || []); } catch { /* */ } };
  const openMembers = async () => { setView('members'); try { const r = await apiService.marketReveal(groupId); setReveal(r?.data?.members || []); } catch { /* */ } };
  const openOffers = async () => { setView('offers'); try { const r = await apiService.marketListOffers(groupId); setOffers(r?.data?.offers || []); } catch { /* */ } };
  const openDeals = async () => { setView('deals'); try { const r = await apiService.marketListDeals(groupId); setDeals(r?.data?.deals || []); } catch { /* */ } };
  const postOffer = async () => {
    try {
      await apiService.marketPostOffer(groupId, {
        commodity: offerForm.commodity.trim() || undefined,
        quantity: offerForm.quantity ? Number(offerForm.quantity) : undefined,
        unit: offerForm.unit.trim() || undefined,
        price: offerForm.price ? Number(offerForm.price) : undefined,
        note: offerForm.note.trim() || undefined,
      });
      setOfferForm({ commodity: '', quantity: '', unit: '', price: '', note: '' });
      await openOffers();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not post offer'); }
  };
  const withdrawOffer = async (id: string) => { try { await apiService.marketWithdrawOffer(groupId, id); await openOffers(); } catch { /* */ } };
  const recordDeal = async () => {
    if (!dealForm.sellerMemberId || !dealForm.buyerMemberId) { setError('Pick a seller and a buyer'); return; }
    try {
      await apiService.marketRecordDeal(groupId, {
        sellerMemberId: dealForm.sellerMemberId, buyerMemberId: dealForm.buyerMemberId,
        commodity: dealForm.commodity.trim() || undefined,
        quantity: dealForm.quantity ? Number(dealForm.quantity) : undefined,
        price: dealForm.price ? Number(dealForm.price) : undefined,
        commissionAmount: dealForm.commissionAmount ? Number(dealForm.commissionAmount) : undefined,
        commissionPercent: dealForm.commissionPercent ? Number(dealForm.commissionPercent) : undefined,
        note: dealForm.note.trim() || undefined,
      });
      setDealForm({ sellerMemberId: '', buyerMemberId: '', commodity: '', quantity: '', price: '', commissionAmount: '', commissionPercent: '', note: '' });
      await openDeals();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not record deal'); }
  };
  const removeMember = async (memberId: string) => { try { await apiService.marketRemoveMember(groupId, memberId); await openMembers(); await loadGroup(); } catch (e) { setError(e instanceof Error ? e.message : 'Could not remove'); } };
  const decide = async (memberId: string, ok: boolean) => {
    try { ok ? await apiService.marketApprove(groupId, memberId) : await apiService.marketReject(groupId, memberId); await openRequests(); await loadGroup(); await loadMessages(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); }
  };

  if (loading) return <div style={st.loading}>Loading…</div>;

  const notMember = !canPost && !isMediator;
  const pending = group?.viewerStatus === 'requested';

  return (
    <div style={st.page}>
      <div style={st.top}>
        <button style={st.back} onClick={() => navigate('/customer/marketplace')}>←</button>
        <div style={{ minWidth: 0 }}>
          <div style={st.title}>{group?.title}</div>
          <div style={st.meta}>{group?.commodity ? `🏷️ ${group.commodity} · ` : ''}{group?.memberCount ?? 0} members{isMediator ? ' · you mediate' : group?.viewerRole ? ` · you: ${group.viewerRole}` : ''}</div>
        </div>
      </div>

      {canPost && (
        <div style={st.mtabs}>
          <button style={view === 'chat' ? st.mtabOn : st.mtab} onClick={() => setView('chat')}>Chat</button>
          <button style={view === 'offers' ? st.mtabOn : st.mtab} onClick={openOffers}>Offers</button>
          {isMediator && <button style={view === 'requests' ? st.mtabOn : st.mtab} onClick={openRequests}>Requests{pendingCount ? ` (${pendingCount})` : ''}</button>}
          {isMediator && <button style={view === 'deals' ? st.mtabOn : st.mtab} onClick={openDeals}>Deals</button>}
          {isMediator && <button style={view === 'members' ? st.mtabOn : st.mtab} onClick={openMembers}>Who’s who</button>}
        </div>
      )}

      {error && <div style={st.error}>{error}</div>}

      {notMember ? (
        <div style={st.joinCard}>
          <div style={st.joinTitle}>Join this group</div>
          <p style={st.joinSub}>{pending ? 'Your request is pending the mediator’s approval.' : 'Ask the mediator to join. Other members won’t see your identity — only the mediator will.'}</p>
          {!pending && (
            <div style={st.joinBtns}>
              <button style={st.joinBtn} onClick={() => requestJoin('seller')}>Request as Seller</button>
              <button style={{ ...st.joinBtn, background: '#7c3aed' }} onClick={() => requestJoin('buyer')}>Request as Buyer</button>
            </div>
          )}
        </div>
      ) : view === 'requests' ? (
        <div style={st.panel}>
          {requests.length === 0 ? <div style={st.empty}>No pending requests.</div> : requests.map((r) => (
            <div key={r.memberId} style={st.reqRow}>
              <div>
                <div style={st.reqName}>{r.name} <span style={{ ...st.pill, color: roleColor(r.role) }}>{r.role}</span></div>
                {r.phone && <div style={st.reqPhone}>{r.phone}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={st.approve} onClick={() => decide(r.memberId, true)}>Approve</button>
                <button style={st.reject} onClick={() => decide(r.memberId, false)}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      ) : view === 'members' ? (
        <div style={st.panel}>
          <p style={st.revealNote}>🔒 Only you can see this. Members see each other only as aliases.</p>
          {reveal.length === 0 ? <div style={st.empty}>No members yet.</div> : reveal.map((m) => (
            <div key={m.memberId} style={st.reqRow}>
              <div><div style={st.reqName}>{m.name} <span style={{ ...st.pill, color: roleColor(m.role) }}>{m.role}</span> <span style={st.aliasTag}>{m.alias}</span></div>{m.phone && <div style={st.reqPhone}>{m.phone}</div>}</div>
              <button style={st.reject} onClick={() => removeMember(m.memberId)}>Remove</button>
            </div>
          ))}
        </div>
      ) : view === 'offers' ? (
        <div style={st.panel}>
          {!isMediator && (
            <div style={st.offerForm}>
              <div style={st.offerFormTitle}>Post an offer</div>
              <div style={st.offerGrid}>
                <input style={st.miniInput} placeholder="Commodity" value={offerForm.commodity} onChange={(e) => setOfferForm({ ...offerForm, commodity: e.target.value })} />
                <input style={st.miniInput} type="number" placeholder="Qty" value={offerForm.quantity} onChange={(e) => setOfferForm({ ...offerForm, quantity: e.target.value })} />
                <input style={st.miniInput} placeholder="Unit (kg…)" value={offerForm.unit} onChange={(e) => setOfferForm({ ...offerForm, unit: e.target.value })} />
                <input style={st.miniInput} type="number" placeholder="Price" value={offerForm.price} onChange={(e) => setOfferForm({ ...offerForm, price: e.target.value })} />
              </div>
              <input style={{ ...st.miniInput, width: '100%', marginTop: 6 }} placeholder="Note (optional)" value={offerForm.note} onChange={(e) => setOfferForm({ ...offerForm, note: e.target.value })} />
              <button style={st.offerPost} onClick={() => void postOffer()}>Post offer</button>
            </div>
          )}
          {offers.length === 0 ? <div style={st.empty}>No open offers yet.</div> : offers.map((o) => (
            <div key={o.id} style={st.reqRow}>
              <div>
                <div style={st.reqName}><span style={{ ...st.pill, color: roleColor(o.side), marginLeft: 0 }}>{o.side}</span> {o.commodity || 'Item'}{o.quantity != null ? ` · ${o.quantity}${o.unit ? ' ' + o.unit : ''}` : ''}{o.price != null ? ` · ${o.price}` : ''}</div>
                <div style={st.reqPhone}>{o.senderAlias}{o.note ? ` — ${o.note}` : ''}</div>
              </div>
              {o.isYou && <button style={st.reject} onClick={() => withdrawOffer(o.id)}>Withdraw</button>}
            </div>
          ))}
        </div>
      ) : view === 'deals' ? (
        <div style={st.panel}>
          <div style={st.offerForm}>
            <div style={st.offerFormTitle}>Record a deal (your commission)</div>
            <select style={{ ...st.miniInput, width: '100%', marginBottom: 6 }} value={dealForm.sellerMemberId} onChange={(e) => setDealForm({ ...dealForm, sellerMemberId: e.target.value })}>
              <option value="">Seller…</option>
              {roster.filter((r) => r.role === 'seller').map((r) => <option key={r.memberId} value={r.memberId}>{r.alias}{r.name ? ` · ${r.name}` : ''}</option>)}
            </select>
            <select style={{ ...st.miniInput, width: '100%', marginBottom: 6 }} value={dealForm.buyerMemberId} onChange={(e) => setDealForm({ ...dealForm, buyerMemberId: e.target.value })}>
              <option value="">Buyer…</option>
              {roster.filter((r) => r.role === 'buyer').map((r) => <option key={r.memberId} value={r.memberId}>{r.alias}{r.name ? ` · ${r.name}` : ''}</option>)}
            </select>
            <div style={st.offerGrid}>
              <input style={st.miniInput} placeholder="Commodity" value={dealForm.commodity} onChange={(e) => setDealForm({ ...dealForm, commodity: e.target.value })} />
              <input style={st.miniInput} type="number" placeholder="Qty" value={dealForm.quantity} onChange={(e) => setDealForm({ ...dealForm, quantity: e.target.value })} />
              <input style={st.miniInput} type="number" placeholder="Price" value={dealForm.price} onChange={(e) => setDealForm({ ...dealForm, price: e.target.value })} />
              <input style={st.miniInput} type="number" placeholder="Commission" value={dealForm.commissionAmount} onChange={(e) => setDealForm({ ...dealForm, commissionAmount: e.target.value })} />
            </div>
            <button style={st.offerPost} onClick={() => void recordDeal()}>Record deal</button>
          </div>
          {deals.length === 0 ? <div style={st.empty}>No deals recorded yet.</div> : deals.map((d) => (
            <div key={d.id} style={st.reqRow}>
              <div>
                <div style={st.reqName}>{d.sellerAlias} → {d.buyerAlias} {d.commodity ? `· ${d.commodity}` : ''}</div>
                <div style={st.reqPhone}>{d.quantity != null ? `${d.quantity} @ ${d.price ?? '—'}` : ''}{d.commissionAmount ? ` · commission ${d.commissionAmount}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div ref={listRef} style={st.thread}>
            {messages.length === 0 && <div style={st.empty}>No messages yet. Say hello 👋</div>}
            {messages.map((m) => m.kind === 'system' ? (
              <div key={m.id} style={st.system}>{m.text}</div>
            ) : (
              <div key={m.id} style={{ ...st.bubbleRow, justifyContent: m.isYou ? 'flex-end' : 'flex-start' }}>
                <div style={{ ...st.bubble, ...(m.isYou ? st.bubbleMine : {}) }}>
                  {!m.isYou && <div style={{ ...st.bubbleName, color: roleColor(m.senderRole) }}>{m.senderAlias}</div>}
                  <div>{m.text}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={st.composer}>
            <input style={st.composerInput} placeholder="Message the group…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void send(); }} />
            <button style={st.sendBtn} onClick={() => void send()} disabled={!text.trim() || sending}>Send</button>
          </div>
        </>
      )}
    </div>
  );
};

const st: Record<string, React.CSSProperties> = {
  page: { position: 'fixed', inset: 0, background: '#f6f7f9', display: 'flex', flexDirection: 'column' },
  loading: { padding: 40, textAlign: 'center', color: '#94a3b8' },
  top: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#fff', borderBottom: '1px solid #eef1f5' },
  back: { width: 34, height: 34, borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', fontSize: 16, cursor: 'pointer', flex: '0 0 auto' },
  title: { fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  meta: { fontSize: '0.74rem', color: '#64748b' },
  mtabs: { display: 'flex', gap: 6, padding: '10px 14px 0', background: '#fff', overflowX: 'auto' },
  offerForm: { background: '#fff', border: '1px solid #eef1f5', borderRadius: 12, padding: 12 },
  offerFormTitle: { fontWeight: 800, fontSize: '0.85rem', color: '#0f172a', marginBottom: 8 },
  offerGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 },
  miniInput: { padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.85rem', fontFamily: 'inherit' },
  offerPost: { marginTop: 8, width: '100%', border: 'none', borderRadius: 8, padding: '9px 0', fontWeight: 800, color: '#fff', background: 'var(--accent-color)', cursor: 'pointer' },
  mtab: { flex: 1, padding: '8px 0', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },
  mtabOn: { flex: 1, padding: '8px 0', borderRadius: 8, border: '1px solid var(--accent-color)', background: 'var(--accent-soft, #fdeee7)', color: 'var(--accent-color)', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer' },
  error: { background: '#fef2f2', color: '#b91c1c', padding: '8px 14px', fontSize: '0.82rem' },
  joinCard: { margin: 16, background: '#fff', border: '1px solid #eef1f5', borderRadius: 14, padding: 20, textAlign: 'center' },
  joinTitle: { fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' },
  joinSub: { color: '#64748b', fontSize: '0.85rem', margin: '6px 0 16px' },
  joinBtns: { display: 'flex', gap: 10, justifyContent: 'center' },
  joinBtn: { border: 'none', borderRadius: 10, padding: '11px 16px', fontWeight: 800, color: '#fff', background: '#0369a1', cursor: 'pointer' },
  panel: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 },
  revealNote: { fontSize: '0.8rem', color: 'var(--accent-color)', background: 'var(--accent-soft, #fdeee7)', borderRadius: 8, padding: '8px 12px', margin: 0 },
  reqRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#fff', border: '1px solid #eef1f5', borderRadius: 12, padding: 12 },
  reqName: { fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' },
  reqPhone: { fontSize: '0.78rem', color: '#64748b', marginTop: 2 },
  pill: { fontSize: '0.66rem', fontWeight: 800, textTransform: 'capitalize', marginLeft: 6 },
  aliasTag: { fontSize: '0.72rem', fontWeight: 800, color: '#334155', background: '#f1f5f9', borderRadius: 999, padding: '3px 10px' },
  approve: { border: 'none', borderRadius: 8, padding: '7px 12px', fontWeight: 800, color: '#fff', background: '#16a34a', cursor: 'pointer' },
  reject: { border: '1px solid #fecaca', borderRadius: 8, padding: '7px 12px', fontWeight: 700, color: '#dc2626', background: '#fff', cursor: 'pointer' },
  thread: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  system: { alignSelf: 'center', fontSize: '0.74rem', color: '#94a3b8', fontStyle: 'italic', background: '#eef2f7', borderRadius: 999, padding: '3px 12px' },
  bubbleRow: { display: 'flex' },
  bubble: { maxWidth: '78%', background: '#fff', border: '1px solid #eef1f5', borderRadius: 14, padding: '8px 12px', fontSize: '0.9rem', color: '#0f172a' },
  bubbleMine: { background: 'var(--accent-color)', color: '#fff', border: 'none' },
  bubbleName: { fontSize: '0.7rem', fontWeight: 800, marginBottom: 2 },
  composer: { display: 'flex', gap: 8, padding: 12, background: '#fff', borderTop: '1px solid #eef1f5' },
  composerInput: { flex: 1, padding: '11px 14px', borderRadius: 999, border: '1px solid #e2e8f0', fontSize: '0.9rem' },
  sendBtn: { border: 'none', borderRadius: 999, padding: '0 18px', fontWeight: 800, color: '#fff', background: 'var(--accent-color)', cursor: 'pointer' },
  empty: { textAlign: 'center', color: '#94a3b8', padding: '40px 12px' },
};

export default MarketGroup;
